import React, { useEffect, useState } from 'react';
import useContact from '@afosto/queries/contact/useContact';
import useHandlingList from '@afosto/queries/fulfilment/useHandlingList';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useShipment from '@afosto/queries/fulfilment/useShipment';
import useShippingMethod from '@afosto/queries/fulfilment/useShippingMethod';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useQueryCache } from 'react-query';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import fetchShipmentAddressing from '../../utils/fetchShipmentAddressing';
import ShipmentDetails from './ShipmentDetails';
import ShipmentItems from './ShipmentItems';
import ShipmentLogs from './ShipmentLogs';
import ShipmentOverage from './ShipmentOverage';
import ShipmentParcels from './ShipmentParcels/ShipmentParcels';
import ShipmentProvider from '../../components/ShipmentProvider';
import Page from '@afosto/app-components-react/Page';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import { createHandlingList, startPicking, startPlacing } from '../../utils/handlingList';
import wait from '@afosto/utils/wait';
import { handling as handlingApi } from '@afosto/fulfilment/api/Handling';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';
import DataFlowDrawer from '@afosto/app-components-react/DataFlowDrawer';

const ViewShipment = () => {
  usePrivate();

  const confirm = useConfirm();
  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const queryCache = useQueryCache();
  const { id: shipmentID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:handling:read',
    'lcs:shipments:read',
    'lcs:locations:read',
    'rel:contacts:read',
  ]);
  const [isFetchingAddress, setIsFetchingAddress] = useState(true);
  const [isFinishingShipment, setIsFinishingShipment] = useState(false);
  const [resolvedAddresses, setResolvedAddresses] = useState(null);
  const [showDataFlowDrawer, setShowDataFlowDrawer] = useState(false);
  const { data: shipment, isLoading: isFetchingShipment } = useShipment(shipmentID);
  const orderIds = shipment?.order_id ? [shipment?.order_id] : [];
  const {
    data: handlingList,
    isLoading: isFetchingHandlingList,
  } = useHandlingList(shipment?.list_id, { enabled: shipment?.list_id });
  const { data: contact, isLoading: isFetchingContact } = useContact(shipment?.contact_id, {
    enabled: shipment?.contact_id,
  });
  const { data: method, isLoading: isFetchingMethod } = useShippingMethod(shipment?.method_id, {
    enabled: shipment?.method_id,
  });

  useEffect(() => {
    /**
     * Fetch addressing
     * @returns {Promise<Array>}
     */
    const fetchAddressing = async () => {
      setIsFetchingAddress(true);

      try {
        const addressingResponses = await fetchShipmentAddressing(shipment);
        setResolvedAddresses(addressingResponses);
      } catch (error) {
        setResolvedAddresses({});
      } finally {
        setIsFetchingAddress(false);
      }
    };

    if (shipment) {
      fetchAddressing();
    }
  }, [shipment]);

  const handleFinishShipment = async () => {
    try {
      setIsFinishingShipment(true);

      let updateHandlingList = handlingList;

      if (!shipment?.list_id) {
        updateHandlingList = await createHandlingList(shipment);
      }

      if (!updateHandlingList) {
        throw new Error('No handling list');
      }

      const latestHandlingListResponse = await handlingApi.viewHandlingList(updateHandlingList?.id);
      const latestHandlingList = latestHandlingListResponse?.data;

      if (!latestHandlingList) {
        throw new Error('No handling list');
      }

      const items = (latestHandlingList?.items ?? []).reduce((acc, item) => {
        const itemsToUpdate = (item?.ids ?? []).map(itemId => ({
          id: itemId,
          is_handled: true,
          is_finished: true,
          position: item.position,
        }));

        return [...acc, ...itemsToUpdate];
      }, []);

      await handlingApi.updateHandlingListItem(latestHandlingList?.id, items);
      await wait(2000);

      await queryCache.invalidateQueries(
        query => query.queryKey[0] === 'shipment' && query.queryKey[1] === shipment?.id,
      );

      enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentFinished), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentNotFinished), {
        variant: 'error',
      });
    } finally {
      setIsFinishingShipment(false);
    }
  };

  const handleUpdatePositions = async () => {
    try {
      const response = await confirm({
        title: intl.formatMessage(translations.updatePositionsDialog.title),
        description: intl.formatMessage(translations.updatePositionsDialog.description),
        disableBackdropClick: true,
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.updatePositionsDialog.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.updatePositionsDialog.buttons.update),
            type: 'submit',
            response: 'update',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (response === 'cancel') {
        return;
      }

      await shipmentsApi.updateShipmentPositions(shipment?.id);

      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.positionsNotUpdated), {
        variant: 'error',
      });
    }
  };

  const handleCancelShipment = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.cancelShipmentDialog.title),
        description: intl.formatMessage(translations.cancelShipmentDialog.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.cancelShipmentDialog.buttons.close),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.cancelShipmentDialog.buttons.cancelShipment),
            response: 'cancelShipment',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'cancelShipment') {
        await shipmentsApi.cancelShipment(shipment?.id);

        enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentCancelled), {
          variant: 'success',
        });

        await queryCache.invalidateQueries(
          query => query.queryKey[0] === 'shipment' && query.queryKey[1] === shipment?.id,
        );
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.shipmentNotCancelled), {
        variant: 'error',
      });
    }
  };

  const handleTriggerDataFlow = () => setShowDataFlowDrawer(true);

  let primaryAction = null;

  if (
    shipment?.is_editable &&
    !shipment?.is_canceled &&
    location.pathname === `/shipments/${shipmentID}`
  ) {
    primaryAction = {
      text: intl.formatMessage(translations.actions.edit),
      action: () => history.push(`/shipments/edit/${shipmentID}`),
      icon: ['fal', 'pencil'],
    };
  }

  if (
    location.pathname === `/shipments/${shipmentID}/items` &&
    !isFetchingHandlingList &&
    !handlingList?.is_finished &&
    !shipment?.is_canceled
  ) {
    if (shipment?.is_outbound) {
      const hasMultipleShipments = handlingList?.shipments?.length > 1;

      if (!hasMultipleShipments && !shipment.list_id) {
        primaryAction = {
          text: intl.formatMessage(translations.actions.pick),
          FABVariant: 'extended',
          actions: [
            {
              text: intl.formatMessage(translations.actions.pick),
              action: () =>
                startPicking({
                  shipment,
                  handlingListID: shipment.list_id,
                  history,
                }),
              icon: ['fal', 'inventory'],
            },
            {
              text: intl.formatMessage(translations.actions.pickAndBox),
              action: () =>
                startPicking({
                  shipment,
                  handlingListID: shipment.list_id,
                  history,
                  pack: true,
                }),
              icon: ['fal', 'hand-holding-box'],
            },
          ],
        };
      } else {
        primaryAction = {
          text: intl.formatMessage(translations.actions.pick),
          action: () =>
            startPicking({
              shipment,
              handlingListID: shipment.list_id,
              history,
            }),
          FABVariant: 'extended',
        };
      }
    }

    if (shipment && !shipment.is_outbound) {
      primaryAction = {
        text: intl.formatMessage(translations.actions.place),
        action: () =>
          startPlacing({
            shipment,
            handlingListID: shipment.list_id,
            history,
          }),
        FABVariant: 'extended',
      };
    }
  }

  const allowPack =
    !shipment?.is_canceled &&
    ((shipment?.is_outbound && shipment?.is_handled) ||
      (!shipment?.is_outbound && !shipment?.is_handled));

  if (
    location.pathname === `/shipments/${shipmentID}/parcels` &&
    allowPack &&
    !shipment?.is_boxed &&
    !shipment?.is_canceled
  ) {
    primaryAction = {
      text: intl.formatMessage(
        translations.actions[shipment && !shipment.is_outbound ? 'unpack' : 'pack'],
      ),
      action: () => {
        if (shipment && !shipment.is_outbound) {
          history.push(`/unpacking/${shipmentID}`);
        } else {
          history.push(`/packing/${shipmentID}`);
        }
      },
      FABVariant: 'extended',
    };
  }

  const secondaryActions = [
    ...(shipment &&
    !shipment?.is_handled &&
    !shipment?.is_finished &&
    !shipment?.is_canceled &&
    !handlingList?.is_in_progress
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.updatePositions),
            action: handleUpdatePositions,
          },
          ...(shipment?.is_outbound
            ? [
                {
                  text: intl.formatMessage(translations.secondaryActions.finishShipment),
                  action: handleFinishShipment,
                },
              ]
            : []),
        ]
      : []),
    {
      text: intl.formatMessage(translations.secondaryActions.triggerDataFlow),
      action: handleTriggerDataFlow,
    },
    ...(!handlingList && !shipment?.is_canceled && !shipment?.is_finished
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.cancelShipment),
            action: handleCancelShipment,
            color: 'secondary',
          },
        ]
      : []),
  ];

  if (!hasPermissions()) {
    return (
      <Page title={shipment?.number || shipment?.id} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  if (isFetchingShipment) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8} xl={9}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4} xl={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SkeletonPage>
    );
  }

  if (!shipment) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        Shipment not found
      </SkeletonPage>
    );
  }

  return (
    <ShipmentProvider
      value={{
        shipment,
        isFetchingShipment,
        addresses: resolvedAddresses,
        isFetchingAddress,
        contact,
        isFetchingContact,
        method,
        isFetchingMethod,
        handlingList,
        isFetchingHandlingList,
        orderIds,
      }}
    >
      <Page
        title={(shipment && shipment.number) || (shipment && shipment.id)}
        fullWidth
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/shipments/${shipmentID}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.items),
              path: `/shipments/${shipmentID}/items`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.parcels),
              path: `/shipments/${shipmentID}/parcels`,
            },
            ...(!shipment?.is_outbound && shipment?.overage?.length
              ? [
                  {
                    label: intl.formatMessage(translations.subNavigation.overage),
                    path: `/shipments/${shipmentID}/overage`,
                  },
                ]
              : []),
            {
              label: intl.formatMessage(translations.subNavigation.logs),
              path: `/shipments/${shipmentID}/logs`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumb.shipments),
            path: '/shipments',
          },
          {
            label: shipment?.number ?? shipment?.id,
            path: `/shipments/${shipmentID}`,
            active: true,
          },
        ]}
        linkComponent={Link}
        useBreadcrumbNavigation={false}
        primaryAction={hasPermissions(['lcs:shipments:write']) ? primaryAction : null}
        secondaryActions={secondaryActions}
      >
        <Switch>
          <Route component={ShipmentItems} path="/shipments/:id/items" />
          <Route component={ShipmentParcels} path="/shipments/:id/parcels" />
          <Route component={ShipmentOverage} path="/shipments/:id/overage" />
          <Route component={ShipmentLogs} path="/shipments/:id/logs" />
          <Route component={ShipmentDetails} path="/shipments/:id" />
        </Switch>
      </Page>
      <ProgressDialog
        isOpen={isFinishingShipment}
        message={intl.formatMessage(translations.finishShipmentDialog.message)}
      />
      <DataFlowDrawer
        open={showDataFlowDrawer}
        filter="shipment"
        onClose={() => setShowDataFlowDrawer(false)}
        onFinish={() => setShowDataFlowDrawer(false)}
        entityId={shipmentID}
      />
    </ShipmentProvider>
  );
};

export default ViewShipment;
